import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { PaktLockup } from '../components/pakt'
import { getCountryConfig } from '../lib/country-config'
import type { Document } from '../types'
import { Tour } from '../components/Tour'

const statusColors: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700',
  PENDING: 'bg-yellow-100 text-yellow-800',
  PARTIAL: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  EXPIRED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-red-100 text-red-700',
}

export default function Dashboard() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()

  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [userName, setUserName] = useState('')
  const [showWelcome, setShowWelcome] = useState(false)

  // Plano selecionado na pricing page (?plan=starter) que o user
  // gravou ao se cadastrar. Banner persiste até user dispensar OU até
  // user.plano coincidir (admin ativou manualmente ou Stripe sub real).
  const [selectedPlan, setSelectedPlan] = useState<{
    plano: string
    displayName: string
    priceBrlMonthly: number
  } | null>(null)

  useEffect(() => {
    try {
      const raw = localStorage.getItem('pakt_selected_plan')
      if (raw) setSelectedPlan(JSON.parse(raw))
    } catch {}
  }, [])

  function dismissSelectedPlan() {
    localStorage.removeItem('pakt_selected_plan')
    setSelectedPlan(null)
  }


  // Set language from user's country on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('pakt_user')
      if (stored) {
        const user = JSON.parse(stored)
        const config = getCountryConfig(user.countryCode || 'BR')
        if (config?.locale && i18n.language !== config.locale) {
          i18n.changeLanguage(config.locale)
          localStorage.setItem('pakt_language', config.locale)
        }
      }
    } catch {}
  }, [])

  // Check if first visit for onboarding modal
  useEffect(() => {
    if (!localStorage.getItem('pakt_onboarded')) {
      setShowWelcome(true)
    }
  }, [])

  function dismissWelcome() {
    localStorage.setItem('pakt_onboarded', 'true')
    setShowWelcome(false)
  }

  function handleWelcomeCTA() {
    localStorage.setItem('pakt_onboarded', 'true')
    setShowWelcome(false)
    navigate('/documents/new')
  }

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    try {
      const res = await api.get<{ documents: Document[]; pagination?: any }>('/api/documents')
      setDocuments((res.documents || []).filter((d: any) => d.status !== 'CANCELLED'))
      const stored = localStorage.getItem('pakt_user')
      if (stored) {
        try { setUserName(JSON.parse(stored).nome) } catch { /* ignore */ }
      }
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'status' in err && (err as { status: number }).status === 401) {
        localStorage.removeItem('pakt_token')
        navigate('/login')
        return
      }
      setError(t('dashboard.loadError', 'Failed to load documents.'))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    const token = localStorage.getItem('pakt_token')
    if (!token) { navigate('/login'); return }
    fetchData()
  }, [navigate])

  function handleLogout() {
    localStorage.removeItem('pakt_token')
    localStorage.removeItem('pakt_user')
    navigate('/login')
  }

  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)

  async function handleDelete(id: string) {
    const token = localStorage.getItem('pakt_token')
    setDeletingId(id)
    try {
      await fetch(`/api/documents/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      setDocuments(prev => prev.filter(d => d.id !== id))
    } catch {
      // ignore
    } finally {
      setDeletingId(null)
      setConfirmDelete(null)
    }
  }

  const stats = {
    total: documents.length,
    pending: documents.filter((d) => d.status === 'PENDING' || d.status === 'PARTIAL').length,
    completed: documents.filter((d) => d.status === 'COMPLETED').length,
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Welcome Onboarding Modal */}
      {showWelcome && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
                        <div className="text-4xl mb-4">👋</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {t('onboarding.welcome', 'Welcome to PAKT')}
              {userName ? `, ${userName}` : ''}!
            </h2>
            <p className="text-gray-500 mb-6">
              {t('onboarding.subtitle', 'Get your first document signed in 3 simple steps:')}
            </p>

            <div className="text-left space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-bold">1</span>
                <p className="text-gray-700 text-sm pt-0.5">{t('onboarding.step1', 'Upload a PDF contract')}</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-bold">2</span>
                <p className="text-gray-700 text-sm pt-0.5">{t('onboarding.step2', 'Add the signer\'s email address')}</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-bold">3</span>
                <p className="text-gray-700 text-sm pt-0.5">{t('onboarding.step3', 'Click "Create document" and you\'re done')}</p>
              </div>
            </div>

            <button
              onClick={handleWelcomeCTA}
              className="w-full py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition mb-3"
            >
              {t('onboarding.cta', 'Send my first document →')}
            </button>
            <button
              onClick={dismissWelcome}
              className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition"
            >
              {t('onboarding.later', 'See later')}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/dashboard"><PaktLockup size={20} /></Link>
          <div className="flex items-center gap-4">
            {userName && (
              <span className="text-sm text-gray-600 hidden sm:inline">
                {t('dashboard.hello', 'Hello')}, <span className="font-medium text-gray-900">{userName}</span>
              </span>
            )}
            <Link to="/ajuda" className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition" title="Ajuda + rever tour">
              ?
            </Link>
            <a href="/settings/api" className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition">API</a>
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {t('dashboard.logout', 'Logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Banner de ativação de plano — aparece quando user veio da
            pricing page com ?plan=starter|pro mas ainda está em FREE.
            Stripe sub real ainda não existe — por enquanto CTA aponta
            pra e-mail comercial. Quando subscription estiver no ar,
            virar link pra /billing/checkout. */}
        {selectedPlan && (
          <div className="mb-6 bg-gradient-to-r from-indigo-50 to-white border border-indigo-200 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1">
                  Plano pendente de ativação
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">
                  Você selecionou{' '}
                  <span className="text-indigo-700">{selectedPlan.displayName}</span>
                  {' '}— R$ {selectedPlan.priceBrlMonthly.toLocaleString('pt-BR')}/mês
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Sua conta foi criada no plano grátis. Pra ativar os recursos do{' '}
                  <strong>{selectedPlan.displayName}</strong>, fale com a gente — confirmação em até 1 dia útil.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={`mailto:comercial@pakt.io?subject=${encodeURIComponent(
                      `Ativação ${selectedPlan.displayName}`,
                    )}&body=${encodeURIComponent(
                      `Olá, gostaria de ativar o plano ${selectedPlan.displayName} ` +
                      `(R$ ${selectedPlan.priceBrlMonthly}/mês).\n\n` +
                      `Conta: ${userName || '[seu nome]'}\n` +
                      `E-mail: ${(() => {
                        try {
                          const u = localStorage.getItem('pakt_user')
                          return u ? JSON.parse(u).email : ''
                        } catch { return '' }
                      })()}\n`,
                    )}`}
                    className="inline-block px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition"
                  >
                    Solicitar ativação por e-mail
                  </a>
                  <Link
                    to="/precos"
                    className="text-sm text-indigo-600 hover:underline px-2"
                  >
                    trocar de plano
                  </Link>
                  <button
                    onClick={dismissSelectedPlan}
                    className="text-sm text-gray-500 hover:text-gray-700 px-2 ml-auto"
                  >
                    dispensar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <p className="text-sm text-gray-500">{t('dashboard.total', 'Total Documents')}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <p className="text-sm text-gray-500">{t('dashboard.pending', 'Pending')}</p>
            <p className="text-3xl font-bold text-yellow-600 mt-1">{stats.pending}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
            <p className="text-sm text-gray-500">{t('dashboard.completed', 'Completed')}</p>
            <p className="text-3xl font-bold text-green-600 mt-1">{stats.completed}</p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl font-bold text-gray-900">{t('dashboard.documents', 'Documents')}</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm text-gray-600 disabled:opacity-50 flex items-center gap-1.5"
              title="Recarregar documentos"
            >
              <svg className={refreshing ? 'animate-spin' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
              {refreshing ? 'Atualizando…' : 'Atualizar'}
            </button>
            <Link
              to="/documents/new"
              data-tour="new-doc-btn"
              className="px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition text-sm"
            >
              + {t('dashboard.newDocument', 'New Document')}
            </Link>
          </div>
        </div>

        {/* Documents Table */}
        {documents.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
                        <div className="text-4xl mb-4">📄</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('dashboard.empty', 'No documents yet')}
            </h3>
            <p className="text-gray-500 mb-6">
              {t('dashboard.emptyDesc', 'Upload your first document to get started.')}
            </p>
            <Link
              to="/documents/new"
              className="inline-block px-6 py-2.5 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition"
            >
              {t('dashboard.createFirst', 'Create Document')}
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm sm:text-base">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('dashboard.col.name', 'Name')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('dashboard.col.status', 'Status')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      {t('dashboard.col.date', 'Date')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      {t('dashboard.col.country', 'Country')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      {t('dashboard.col.signers', 'Signers')}
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t('dashboard.col.actions', 'Actions')}
                    </th>
                    <th className="px-4 py-3 w-8"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {documents.map((doc) => {
                    const country = getCountryConfig(doc.countryCode)
                    return (
                      <tr key={doc.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900">{doc.nome}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusColors[doc.status] || 'bg-gray-100 text-gray-700'}`}
                          >
                            {t(`dashboard.status.${doc.status}`, doc.status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                          {new Date(doc.createdAt).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-3 text-sm hidden md:table-cell">
                          {country ? `${country.flag} ${country.code}` : doc.countryCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                          {doc.signers?.length || 0}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {doc.status === 'DRAFT' ? (
                            <Link to={`/documents/${doc.id}/edit`}
                              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium">
                              {t('docStatus.sendForSigning', 'Posicionar selos e Enviar')}
                            </Link>
                          ) : doc.status === 'COMPLETED' ? (
                            <div className="flex flex-col gap-1 items-end">
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('pakt_token')
                                    const res = await fetch(`/api/documents/${doc.id}/download?type=final`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    })
                                    if (!res.ok) throw new Error('failed')
                                    const blob = await res.blob()
                                    const url = URL.createObjectURL(blob)
                                    const a = document.createElement('a')
                                    a.href = url; a.download = `${doc.nome}_assinado.pdf`; a.click()
                                    URL.revokeObjectURL(url)
                                  } catch { alert('Erro ao baixar.') }
                                }}
                                className="text-sm text-green-600 hover:text-green-800 font-medium">
                                {t('docStatus.downloadSigned', 'Baixar PDF assinado')}
                              </button>
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('pakt_token')
                                    const res = await fetch(`/api/documents/${doc.id}/download-zip`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    })
                                    if (!res.ok) throw new Error('failed')
                                    const blob = await res.blob()
                                    const url = URL.createObjectURL(blob)
                                    const a = document.createElement('a')
                                    a.href = url; a.download = `${doc.nome}_pakt.zip`; a.click()
                                    URL.revokeObjectURL(url)
                                  } catch { alert('Erro ao baixar.') }
                                }}
                                className="text-xs text-green-500 hover:text-green-700 font-medium">
                                {t('docStatus.downloadZip', 'Baixar ZIP')}
                              </button>
                            </div>
                          ) : (
                            <Link to={`/documents/${doc.id}`}
                              className="text-sm text-gray-600 hover:text-gray-800 font-medium">
                              {t('dashboard.view', 'Ver detalhes')}
                            </Link>
                          )}
                        </td>
                        <td className="px-2 py-3">
                          {confirmDelete === doc.id ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleDelete(doc.id)} disabled={deletingId === doc.id}
                                className="text-xs text-white bg-red-500 hover:bg-red-600 px-2 py-0.5 rounded font-medium transition">
                                {deletingId === doc.id ? '...' : t('common.confirm', 'Sim')}
                              </button>
                              <button onClick={() => setConfirmDelete(null)}
                                className="text-xs text-gray-500 hover:text-gray-700 px-1">
                                {t('common.cancel', 'Não')}
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => setConfirmDelete(doc.id)}
                              title={t('dashboard.delete', 'Apagar documento')}
                              className="text-gray-300 hover:text-red-500 transition p-1 rounded">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Tour guiado — só aparece se: já fechou welcome modal + tem 0 docs +
          nunca completou o tour. Aponta pro botão "Novo documento". */}
      {!loading && !showWelcome && documents.length === 0 && (
        <Tour
          storageKey="pakt_tour_first_doc_dashboard"
          steps={[
            {
              targetSelector: '[data-tour="new-doc-btn"]',
              title: 'Crie seu primeiro documento',
              body: 'Clique aqui pra subir um PDF e adicionar signatários. Leva ~2 minutos do upload ao link pronto pra enviar.',
              placement: 'bottom',
              waitForClick: true,
            },
          ]}
        />
      )}
    </div>
  )
}
